import { useMutation, MutationKey } from "react-query";
import Axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RemoteApiError } from "../useRemote/types";
import {
  VehiclePermissionCheckAffectedBody,
  VehiclePermissionAffectedBooking
} from "../../../components/FleetManagement/types/VehiclePermissions.types";

export type CheckAffectedPermissionsPayload = {
  vehicleId: number;
  body: VehiclePermissionCheckAffectedBody;
};

/**
 * Checks which bookings would be affected by updating vehicle permissions
 * (POST /fleet/vehicles/:id/permissions/check-affected).
 * Permissions are stored in vehicle inventory via PATCH; this is only for showing affected bookings.
 */
export function useRemoteCheckAffectedPermissions() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();

  return useMutation<
    VehiclePermissionAffectedBooking[],
    RemoteApiError,
    CheckAffectedPermissionsPayload
  >(
    ["fleet", "check-affected-permissions"] as MutationKey,
    async ({
      vehicleId,
      body
    }: CheckAffectedPermissionsPayload): Promise<VehiclePermissionAffectedBooking[]> => {
      try {
        const response = await Axios.post<VehiclePermissionAffectedBooking[]>(
          `/fleet/vehicles/${vehicleId}/permissions/check-affected`,
          body
        );
        return response.data ?? [];
      } catch (error) {
        snackbar.enqueueSnackbar(t("There was a server error"), { variant: "error" });
        throw error;
      }
    }
  );
}
